/**
 * Resolves the best-matching `StoreRegionResponse` for a DaData `AddressSuggestion`.
 *
 * Pure/deterministic — no I/O — so `LocationDialog` can call it synchronously on every
 * address-suggestion pick, and it's directly unit-testable.
 *
 * Matching is most-specific-first (CITY → REGION → COUNTRY): within each tier, several
 * candidate name variants from the suggestion are compared case-insensitively against every
 * region of that tier's `type`, and the first tier to produce a match wins. A merchant's
 * regions are usually named after a city/region/country, but DaData returns several textual
 * variants for the same place (a bare name vs. one prefixed with a locale-specific type
 * abbreviation) — comparing against all of them tolerates whichever variant a merchant
 * happened to use when naming their region.
 */
import type { AddressSuggestion, StoreRegionResponse, StoreRegionType } from '../model/settingsTypes';

function norm(value: string | undefined): string | undefined {
  const trimmed = value?.trim().toLowerCase();
  return trimmed || undefined;
}

/**
 * Returns `null` when no tier yields a match — callers should leave the region field as-is
 * (or empty) and let the merchant pick manually; see `LocationDialog`'s
 * `regionNotDetectedHint` helper text (`storeProfileSettings.locationDialog` i18n namespace).
 */
export function matchRegionByAddress(
  suggestion: AddressSuggestion,
  regions: StoreRegionResponse[],
): StoreRegionResponse | null {
  const tiers: Array<{ type: StoreRegionType; candidates: Array<string | undefined> }> = [
    {
      type: 'CITY',
      candidates: [suggestion.city, suggestion.cityWithType, suggestion.settlement, suggestion.settlementWithType],
    },
    {
      type: 'REGION',
      candidates: [suggestion.region, suggestion.regionWithType, suggestion.area, suggestion.areaWithType],
    },
    { type: 'COUNTRY', candidates: [suggestion.country] },
  ];

  for (const tier of tiers) {
    const names = tier.candidates.map(norm).filter((value): value is string => Boolean(value));
    if (names.length === 0) continue;
    const match = regions.find((region) => region.type === tier.type && names.includes(norm(region.name) ?? ''));
    if (match) return match;
  }

  return null;
}
